import { AfterViewInit, Directive, ElementRef, EventEmitter, OnDestroy, inject, input } from '@angular/core';
import { Subscription, auditTime } from 'rxjs';

/**
 * Modifies a p-listbox component to have the group separator as collapsable units
 * Use the class from the component to perform the selections and changes
 */
@Directive({
    standalone: true,
    selector: '[unifiedUiCollapsableListbox]'
})
export class CollapsibleListboxDirective implements AfterViewInit, OnDestroy {
    /**
     * Input to call for reapply the directive after the view init if needed
     */
    public readonly updateOnChanges = input<EventEmitter<void>>(undefined, { alias: "unifiedUiCollapsableListbox" });
    /**
     * Subscription  for updating eventEmitter
     */
    private subscription?: Subscription;

    constructor(private elementRef: ElementRef) {}

    public ngAfterViewInit(): void {
        // call to apply when the view is stable
        this.applyDirective();

        // if the updateOnChanges is provided, listen for reapply the directive on changes like filtering
        const updateOnChanges = this.updateOnChanges();
        if (updateOnChanges) {
            this.subscription = updateOnChanges
                .pipe(
                    // wait for the view to be stable again
                    auditTime(200)
                )
                .subscribe(() => {
                    this.applyDirective();
                });
        }
    }

    private applyDirective(): void {
        // Select the elements with the class applied on group separators
        const groups = this.elementRef.nativeElement.querySelectorAll('.p-listbox-option-group');

        for (const group of groups) {
            // Create and append the button for toggle expansion
            const toggleButton = this.createToggleButton();
            group.append(toggleButton);
            group.style = 'display: flex; justify-content: space-between';

            // adds the eventListener on the button for click events
            group.addEventListener('click', () => {
                const nextGroup = this.getNextGroup(group);
                const elementsToToggle = this.getElementsToToggle(group, nextGroup);

                // For each element, identifies it by id and toggles the display
                for (const element of elementsToToggle) {
                    const elementId = element.getAttribute('id') ?? '';
                    // eslint-disable-next-line unicorn/prefer-query-selector
                    const htmlReference = document.getElementById(elementId);
                    if (htmlReference) {
                        htmlReference.style.display = htmlReference.style.display === 'none' ? '' : 'none';
                    }
                }

                // updates the button state
                toggleButton.className = toggleButton.className === 'listbox-collapsable-group-button pi pi-angle-down' ? 'listbox-collapsable-group-button pi pi-angle-up' : 'listbox-collapsable-group-button pi pi-angle-down';
            });
        }
    }

    /**
     * Create the toggle button
     * @returns styled button element
     */
    private createToggleButton(): HTMLButtonElement {
        const button = document.createElement('button');
        button.className = 'listbox-collapsable-group-button pi pi-angle-up';
        return button;
    }

    /**
     * Gets the next element that has the class for group separators
     * @param group current group
     * @returns the next group separator element
     */
    private getNextGroup(group: Element): Element | null {
        let nextSibling = group.nextElementSibling;
        while (nextSibling && !nextSibling.classList.contains('p-listbox-option-group')) {
            nextSibling = nextSibling.nextElementSibling;
        }
        return nextSibling;
    }

    /**
     * Gets the element to be hidden for a specific group
     * @param start group to start the count
     * @param end group to end the count
     * @returns an array of element to be hidden
     */
    private getElementsToToggle(start: Element, end: Element | null): Element[] {
        const elementsToToggle = [];
        let sibling = start.nextElementSibling;
        while (sibling && sibling !== end) {
            elementsToToggle.push(sibling);
            sibling = sibling.nextElementSibling;
        }
        return elementsToToggle;
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
